import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { usePageTracking } from '../hooks/usePageTracking';
import guaranaImg from '../assets/newFruit.png';
import sugarImg from '../assets/newSugar.png';
import vitaminCImg from '../assets/newVitamin.png';
import './TheProduct.css';

const INGREDIENTS = [
  {
    name: 'GUARANA',
    tagline: 'Natural Energy',
    image: guaranaImg,
    description:
      "Guarana is a natural source of caffeine grown in the Amazon rainforest in Brazil. But unlike common forms of caffeine such as coffee, it doesn't burn out fast.\n\nIt releases energy steadily — helping you stay sharp, focused, and locked in without the jitters or crash. Built for endurance. Built to last.",
  },
  {
    name: 'SUGAR',
    tagline: 'Fast Fuel',
    image: sugarImg,
    description:
      "Sugar is your body's fastest fuel source — it turns into glucose quickly so your muscles get energy right when you need it most. During high-intensity workouts or long endurance sessions, that fast fuel helps power explosive movement, delay fatigue, and keep performance strong. When timing matters, quick carbs mean quicker energy — so you can push harder and last longer.",
  },
  {
    name: 'VITAMIN C',
    tagline: 'Immune Support',
    image: vitaminCImg,
    description:
      'Vitamin C helps support your immune system and recovery — especially when your body is under stress from training, competition, or long days.',
  },
];

const CLAIMS = [
  '200mg of Natural Caffeine from Guarana Extract',
  'Made with Real Sugars. No High Fructose Corn Syrup',
  'Vegan',
  'Contains Vitamin C',
];

const BENEFITS = [
  {
    title: 'Energy',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
    description: 'Boosts stamina and vitality with natural ingredients that help sustain physical and mental performance.',
  },
  {
    title: 'Focus',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
    description: 'Supports mental clarity, concentration, and calm alertness throughout the day.',
  },
  {
    title: 'No Crash',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    description: 'Guarana releases caffeine slowly and steadily — no spikes, no jitters, no crash.',
  },
];

function TheProduct() {
  usePageTracking('the-product');
  const navigate = useNavigate();
  const [labelOpen, setLabelOpen] = useState(false);
  const containerRef = useRef(null);

  // Scroll-triggered animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const el = containerRef.current;
    if (el) {
      el.querySelectorAll('.animate-on-scroll').forEach((child) => {
        observer.observe(child);
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="theproduct-page" ref={containerRef}>
      <BrandLogo />

      {/* Back button */}
      <button className="back-button-simple" onClick={() => navigate('/about')}>
        <span className="back-text">← Back</span>
        <span className="back-arrow">←</span>
      </button>

      <div className="theproduct-container">
        <h1 className="theproduct-heading animate-on-scroll">THE PRODUCT</h1>
        <p className="theproduct-subheading animate-on-scroll">What goes into every Sambas candy</p>

        {/* Ingredient Cards */}
        <div className="ingredient-list">
          {INGREDIENTS.map((ing, i) => (
            <div
              className={`ingredient-card animate-on-scroll ${i % 2 !== 0 ? 'from-right' : 'from-left'}`}
              key={ing.name}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className={`ingredient-inner ${i % 2 !== 0 ? 'reverse' : ''}`}>
                <div className="ingredient-image-wrap">
                  <img src={ing.image} alt={ing.name} className="ingredient-image" />
                </div>
                <div className="ingredient-info">
                  <span className="ingredient-tagline">{ing.tagline}</span>
                  <h2 className="ingredient-name">{ing.name}</h2>
                  <p className="ingredient-desc">
                    {ing.description.split('\n\n').map((para, idx) => (
                      <span key={idx}>
                        {para}
                        {idx < ing.description.split('\n\n').length - 1 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Claims */}
        <div className="claims-section">
          {CLAIMS.map((claim, i) => (
            <div
              className="claim-item animate-on-scroll"
              key={claim}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <span className="claim-check">✓</span>
              <span className="claim-text">{claim}</span>
            </div>
          ))}
        </div>

        {/* Nutrition Label Dropdown */}
        <div className="nutrition-dropdown animate-on-scroll">
          <button
            className={`nutrition-toggle ${labelOpen ? 'open' : ''}`}
            onClick={() => setLabelOpen(!labelOpen)}
          >
            <span>View Nutrition Label</span>
            <span className="nutrition-arrow">{labelOpen ? '−' : '+'}</span>
          </button>
          {labelOpen && (
            <div className="nutrition-content">
              <p>Not available at this time.</p>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h2 className="benefits-heading animate-on-scroll">What It Does For You</h2>
          <div className="benefits-grid">
            {BENEFITS.map((b, i) => (
              <div
                className="benefit-card animate-on-scroll"
                key={b.title}
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="benefit-icon">{b.icon}</div>
                <h3 className="benefit-title">{b.title}</h3>
                <p className="benefit-desc">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TheProduct;
